import React, { useMemo, useState } from 'react';
import './DailyActivityChart.css';

export interface DailyActivityPoint {
    date: string; // YYYY-MM-DD
    submissions: number;
    hits: number;
    criminal?: number;
    civil?: number;
    nonIdent?: number;
}

interface DailyActivityChartProps {
    series: DailyActivityPoint[];
    days?: number;
}

interface Bar {
    date: string;
    label: string;
    submissions: number;
    criminal: number;
    civil: number;
    nonIdent: number;
    pending: number;
}

const VIEW_W = 640;
const VIEW_H = 118;
const PLOT_TOP = 8;
const PLOT_BOTTOM = 98; // baseline y
const LABEL_Y = 114;

const SEGMENTS: { key: keyof Pick<Bar, 'criminal' | 'civil' | 'nonIdent' | 'pending'>; label: string; cls: string }[] = [
    { key: 'criminal', label: 'Criminal', cls: 'chart-bar-criminal' },
    { key: 'civil', label: 'Civil', cls: 'chart-bar-civil' },
    { key: 'nonIdent', label: 'Non-Ident', cls: 'chart-bar-nonident' },
    { key: 'pending', label: 'Pending', cls: 'chart-bar-pending' },
];

/**
 * Daily submissions as a stacked bar per day, broken down by result:
 * criminal idents (red) at the baseline, civil idents (blue), non-idents
 * (green), and still-pending submissions (amber) above — matching the SRF
 * icon colors used elsewhere. Fills the trailing `days` window so quiet days
 * render as gaps rather than vanishing.
 */
const DailyActivityChart: React.FC<DailyActivityChartProps> = ({ series, days = 30 }) => {
    const [hovered, setHovered] = useState<number | null>(null);

    const bars: Bar[] = useMemo(() => {
        const byDate = new Map(series.map(p => [p.date, p]));
        const out: Bar[] = [];
        const now = new Date();
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(now.getDate() - i);
            const key = d.toISOString().substring(0, 10);
            const point = byDate.get(key);
            const submissions = point?.submissions ?? 0;
            const criminal = point?.criminal ?? 0;
            const civil = point?.civil ?? 0;
            const nonIdent = point?.nonIdent ?? 0;
            out.push({
                date: key,
                label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                submissions,
                criminal,
                civil,
                nonIdent,
                pending: Math.max(0, submissions - criminal - civil - nonIdent),
            });
        }
        return out;
    }, [series, days]);

    const maxValue = Math.max(1, ...bars.map(b => b.submissions));
    const slot = VIEW_W / bars.length;
    const barWidth = Math.max(4, Math.min(16, slot - 4));
    const plotHeight = PLOT_BOTTOM - PLOT_TOP;

    const total = bars.reduce((acc, b) => acc + b.submissions, 0);
    const totals = SEGMENTS.map(s => ({ ...s, total: bars.reduce((acc, b) => acc + b[s.key], 0) }));

    return (
        <div className="activity-chart">
            <div className="activity-chart-legend" aria-hidden="true">
                {SEGMENTS.map(s => (
                    <span key={s.key} className="legend-item">
                        <span className={`legend-swatch legend-swatch--${s.key.toLowerCase()}`} /> {s.label}
                    </span>
                ))}
            </div>
            <div className="activity-chart-plot">
                <svg
                    viewBox={`0 0 ${VIEW_W} ${VIEW_H + 4}`}
                    role="img"
                    aria-label={`Daily activity for the last ${days} days: ${total} submissions — ${totals.map(t => `${t.total} ${t.label.toLowerCase()}`).join(', ')}.`}
                    onMouseLeave={() => setHovered(null)}
                >
                    {/* recessive max gridline + baseline */}
                    <line x1={0} y1={PLOT_TOP} x2={VIEW_W} y2={PLOT_TOP} className="chart-gridline" />
                    <text x={0} y={PLOT_TOP - 2} className="chart-axis-text">{maxValue}</text>
                    <line x1={0} y1={PLOT_BOTTOM} x2={VIEW_W} y2={PLOT_BOTTOM} className="chart-baseline" />
                    <text x={0} y={LABEL_Y} className="chart-axis-text">{bars[0]?.label}</text>
                    <text x={VIEW_W} y={LABEL_Y} textAnchor="end" className="chart-axis-text">{bars[bars.length - 1]?.label}</text>

                    {bars.map((bar, i) => {
                        const x = i * slot + (slot - barWidth) / 2;
                        // Stack the result segments bottom-up with a 2px
                        // surface gap between adjacent nonzero segments.
                        let y = PLOT_BOTTOM;
                        const rects = SEGMENTS.filter(s => bar[s.key] > 0).map(s => {
                            const h = (bar[s.key] / maxValue) * plotHeight;
                            y -= h;
                            const rect = <rect key={s.key} x={x} y={y} width={barWidth} height={h} rx={2} className={s.cls} />;
                            y -= 2;
                            return rect;
                        });
                        return (
                            <g
                                key={bar.date}
                                onMouseEnter={() => setHovered(i)}
                                className={hovered === i ? 'chart-bar chart-bar--hover' : 'chart-bar'}
                            >
                                {/* full-height hit target */}
                                <rect x={i * slot} y={PLOT_TOP} width={slot} height={plotHeight} fill="transparent" />
                                {rects}
                            </g>
                        );
                    })}
                </svg>
                {hovered !== null && bars[hovered] && (
                    <div
                        className="activity-chart-tooltip"
                        style={{ left: `${((hovered + 0.5) / bars.length) * 100}%` }}
                        role="status"
                    >
                        <div className="tooltip-date">{bars[hovered].label}</div>
                        <div>{bars[hovered].submissions} submission{bars[hovered].submissions === 1 ? '' : 's'}</div>
                        {SEGMENTS.filter(s => bars[hovered][s.key] > 0).map(s => (
                            <div key={s.key}>{bars[hovered][s.key]} {s.label.toLowerCase()}</div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DailyActivityChart;
